import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AUTOMATION_HUB_TOOL_CLICKED, AUTOMATION_HUB_TOOLS_FILTERED } from '../../../../constants/analytics-events';
import {
  useAutomationRulesSetupCompleteSellerSetting,
  useCustomRateShopperSetupCompleteSellerSetting,
  useIsSmartFillSellerSettingEnabled,
  useProductPresetsSetupCompleteSellerSetting,
  useServiceMappingsSetupCompleteSellerSetting,
  useSmartRoutingSetupCompleteSellerSetting,
} from '../../../../hooks/use-seller-setting';
import { useOrderRoutingFeatureGate } from '../../../../feature-gates/use-order-routing-feature-gate';
import {
  BASE_PRODUCTS_PATH,
  BASE_SETTINGS_PATH,
  getAutomationsSettingsPath,
  getRateShopperSettingsPath,
} from '../../../../routes/constants';
import { Segment } from '../../../../services/analytics-services/segment';
import Global from '../../../../store/global-selectors';

import { ToolActionButton } from '../tool-action-button/tool-action-button';
import { ToolActivityPill } from '../tool-activity-pill/tool-activity-pill';
import { ToolCard } from '../tool-card/tool-card';
import { ToolFilters } from '../tool-filters/tool-filters';
import {
  FILTER_DECIDING_HOW_TO_SHIP,
  FILTER_EVERYTHING,
  FILTER_FULFILLING_AT_SCALE,
  FILTER_PREPARING_ORDERS,
  TOOL_FILTERS,
} from './automation-tools-constants';
import { messages } from './messages';
import style from './automation-tools.module.less';

import type React from 'react';

const ORDER_ROUTING_PATH = `${BASE_SETTINGS_PATH}/smartroutingrules`;
const PRODUCT_PRESETS_PATH = `${BASE_PRODUCTS_PATH}/producttype`;
const RATE_SHOPPER_PATH = getRateShopperSettingsPath();
const AUTOMATION_RULES_PATH = getAutomationsSettingsPath();
const SMART_FILL_PATH = `${BASE_SETTINGS_PATH}/smartfillrules`;
const SERVICE_MAPPINGS_BASE_PATH = `${BASE_SETTINGS_PATH}/stores`;

export const AutomationTools: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState(FILTER_EVERYTHING);
  const navigate = useNavigate();
  const activeStores = useSelector(Global.selectSellerActiveStores);
  const enableSmartRouting = useOrderRoutingFeatureGate();
  const isOrderRoutingSetup = useSmartRoutingSetupCompleteSellerSetting();
  const isProductPresetsSetup = useProductPresetsSetupCompleteSellerSetting();
  const isRateShopperSetup = useCustomRateShopperSetupCompleteSellerSetting();
  const isAutomationRulesSetup = useAutomationRulesSetupCompleteSellerSetting();
  const isSmartFillSetup = useIsSmartFillSellerSettingEnabled();
  const isServiceMappingsSetup = useServiceMappingsSetupCompleteSellerSetting();

  const handleOrderRoutingClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'order-routing' });
    navigate(ORDER_ROUTING_PATH);
  }, [navigate]);

  const handleProductPresetsClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'product-preset-groups' });
    navigate(PRODUCT_PRESETS_PATH);
  }, [navigate]);

  const handleServiceMappingsClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'service-mappings' });
  }, []);

  const handleRateShopperClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'rate-shopper' });
    navigate(RATE_SHOPPER_PATH);
  }, [navigate]);

  const handleAutomationRulesClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'automation-rules' });
    navigate(AUTOMATION_RULES_PATH);
  }, [navigate]);

  const handleSmartFillClick = useCallback(() => {
    Segment.trackEvent(AUTOMATION_HUB_TOOL_CLICKED, { tool: 'smart-fill' });
    navigate(SMART_FILL_PATH);
  }, [navigate]);

  const handleStoreMappingClick = useCallback(
    (storeGuid: string) => {
      navigate(`${SERVICE_MAPPINGS_BASE_PATH}/${storeGuid}/shippingservices`);
    },
    [navigate],
  );

  const handleFilterChange = useCallback((key: string) => {
    setSelectedFilter(key);
    Segment.trackEvent(AUTOMATION_HUB_TOOLS_FILTERED, { filter: key });
  }, []);

  const isVisible = useCallback(
    (toolCategories: string[]) => {
      return selectedFilter === FILTER_EVERYTHING || toolCategories.includes(selectedFilter);
    },
    [selectedFilter],
  );

  return (
    <div className={style['automation-tools']}>
      <ToolFilters filters={TOOL_FILTERS} onFilterChange={handleFilterChange} selectedFilter={selectedFilter} />

      <div className={style['tools-grid']}>
        {enableSmartRouting ? (
          <ToolCard isVisible={isVisible([FILTER_FULFILLING_AT_SCALE])}>
            <FlexRow gap={Gap.Space8}>
              <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.orderRoutingTitle} />
              <ToolActivityPill isSetup={isOrderRoutingSetup} />
            </FlexRow>
            <FlexRow className={style['action-row']} gap={Gap.Space16}>
              <Text className={style['tool-description']} textType={TextType.Caption} value={messages.orderRoutingDescription} />
              <ToolActionButton isSetup={isOrderRoutingSetup} onClick={handleOrderRoutingClick} />
            </FlexRow>
          </ToolCard>
        ) : null}

        <ToolCard isVisible={isVisible([FILTER_PREPARING_ORDERS])}>
          <FlexRow gap={Gap.Space8}>
            <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.productPresetGroupsTitle} />
            <ToolActivityPill isSetup={isProductPresetsSetup} />
          </FlexRow>
          <FlexRow className={style['action-row']} gap={Gap.Space16}>
            <Text className={style['tool-description']} textType={TextType.Caption} value={messages.productPresetGroupsDescription} />
            <ToolActionButton isSetup={isProductPresetsSetup} onClick={handleProductPresetsClick} />
          </FlexRow>
        </ToolCard>

        <ToolCard isVisible={isVisible([FILTER_DECIDING_HOW_TO_SHIP])}>
          <FlexRow gap={Gap.Space8}>
            <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.serviceMappingsTitle} />
            <ToolActivityPill isSetup={isServiceMappingsSetup} />
          </FlexRow>
          <FlexRow className={style['action-row']} gap={Gap.Space16}>
            <Text className={style['tool-description']} textType={TextType.Caption} value={messages.serviceMappingsDescription} />
            <DropdownMenu className={style['tool-action']}>
              <DropdownMenuToggler
                onClick={handleServiceMappingsClick}
                theme={isServiceMappingsSetup ? THEME_CONSTANTS.tertiary : THEME_CONSTANTS.primary}
              >
                <Text textType={TextType.Caption} value={isServiceMappingsSetup ? messages.manage : messages.setUp} />
              </DropdownMenuToggler>
              {activeStores.map((store: { storeGuid: string; name: string }) => (
                <DropdownMenuItem key={store.storeGuid} onClick={() => handleStoreMappingClick(store.storeGuid)}>
                  {store.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </FlexRow>
        </ToolCard>

        <ToolCard isVisible={isVisible([FILTER_DECIDING_HOW_TO_SHIP])}>
          <FlexRow gap={Gap.Space8}>
            <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.rateShopperTitle} />
            <ToolActivityPill isSetup={isRateShopperSetup} />
          </FlexRow>
          <FlexRow className={style['action-row']} gap={Gap.Space16}>
            <Text className={style['tool-description']} textType={TextType.Caption} value={messages.rateShopperDescription} />
            <ToolActionButton isSetup={isRateShopperSetup} onClick={handleRateShopperClick} />
          </FlexRow>
        </ToolCard>

        <ToolCard isVisible={isVisible([FILTER_PREPARING_ORDERS, FILTER_DECIDING_HOW_TO_SHIP, FILTER_FULFILLING_AT_SCALE])}>
          <FlexRow gap={Gap.Space8}>
            <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.automationRulesTitle} />
            <ToolActivityPill isSetup={isAutomationRulesSetup} />
          </FlexRow>
          <FlexRow className={style['action-row']} gap={Gap.Space16}>
            <Text className={style['tool-description']} textType={TextType.Caption} value={messages.automationRulesDescription} />
            <ToolActionButton isSetup={isAutomationRulesSetup} onClick={handleAutomationRulesClick} />
          </FlexRow>
        </ToolCard>

        <ToolCard isVisible={isVisible([FILTER_PREPARING_ORDERS])}>
          <FlexRow gap={Gap.Space8}>
            <Text className={style['tool-title']} textType={TextType.Paragraph} value={messages.smartFillTitle} />
            <ToolActivityPill isSetup={isSmartFillSetup} />
          </FlexRow>
          <FlexRow className={style['action-row']} gap={Gap.Space16}>
            <Text className={style['tool-description']} textType={TextType.Caption} value={messages.smartFillDescription} />
            <ToolActionButton isSetup={isSmartFillSetup} onClick={handleSmartFillClick} />
          </FlexRow>
        </ToolCard>
      </div>
    </div>
  );
};
